import { useState, useCallback } from 'react';
import { verifyPin } from '../lib/supabase';

// ── AUDIO ─────────────────────────────────────────────────────
function createBeep(frequency, duration, type = 'sine', volume = 0.3) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
  } catch (e) { /* audio not available */ }
}

function playKeyBeep()    { createBeep(880, 0.08, 'sine', 0.2); }
function playErrorBuzz()  { createBeep(180, 0.25, 'sawtooth', 0.3); }
function playSuccessChime() {
  [523, 659, 784].forEach((freq, i) => {
    setTimeout(() => createBeep(freq, 0.15, 'sine', 0.25), i * 80);
  });
}

// Single caller since Stage E: the signed-in-student lock, keyed on
// `auth_pin_verified_{id}` (see App.jsx `authPinKey`). `storageKey` is required —
// the retired token flow's `pin_verified_{id}` key must never be written again.
// `onFullSignOut` adds the real sign-out escape hatch for the wrong-person case.
export default function PinScreen({ student, onSuccess, storageKey, onFullSignOut }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem('hw_muted') === 'true');

  function toggleMute() {
    const next = !muted;
    setMuted(next);
    localStorage.setItem('hw_muted', next.toString());
  }

  const handleKey = useCallback(async (digit) => {
    if (pin.length >= 4) return;
    if (!muted) playKeyBeep();
    const next = pin + digit;
    setPin(next);
    setError('');

    if (next.length === 4) {
      if (!storageKey) {
        console.error('PinScreen: storageKey is required — refusing to unlock without one.');
        if (!muted) playErrorBuzz();
        setShake(true);
        setTimeout(() => { setPin(''); setShake(false); setError('Something went wrong — try again'); }, 600);
        return;
      }
      const ok = await verifyPin(next, student.pin_hash);
      if (ok) {
        if (!muted) playSuccessChime();
        localStorage.setItem(storageKey, Date.now().toString());
        setTimeout(() => onSuccess(), 300);
      } else {
        if (!muted) playErrorBuzz();
        setShake(true);
        setTimeout(() => { setPin(''); setShake(false); setError('Wrong PIN — try again'); }, 600);
      }
    }
  }, [pin, muted, student, onSuccess, storageKey]);

  function handleBackspace() {
    if (!muted) playKeyBeep();
    setPin(p => p.slice(0, -1));
    setError('');
  }

  const keys = ['1','2','3','4','5','6','7','8','9','','0','⌫'];

  return (
    <div className="pin-screen">
      {/* Mute toggle */}
      <button
        onClick={toggleMute}
        style={{
          position: 'absolute', top: 16, right: 16,
          background: 'rgba(255,255,255,0.15)', border: 'none',
          borderRadius: 20, padding: '6px 12px',
          color: 'white', fontSize: 18, cursor: 'pointer'
        }}
        title={muted ? 'Unmute sounds' : 'Mute sounds'}
      >
        {muted ? '🔇' : '🔊'}
      </button>

      <svg style={{ width: 80, height: 80 }} viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="38" fill="#e32017"/>
        <rect x="2" y="29" width="76" height="22" fill="#003688"/>
        <text x="40" y="44" textAnchor="middle" fontSize="12" fill="white" fontWeight="700" fontFamily="Arial">HOMEWORK</text>
      </svg>

      <div style={{ textAlign: 'center' }}>
        <h1 style={{ color: 'white', fontSize: 24, marginBottom: 6 }}>{student.name}'s Planner</h1>
        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Enter your PIN to board</p>
      </div>

      <div className={`pin-dots${shake ? ' shake' : ''}`}>
        {[0,1,2,3].map(i => (
          <div key={i} className={`pin-dot ${i < pin.length ? 'filled' : ''}`} />
        ))}
      </div>

      {error && <div className="pin-error">{error}</div>}

      <div className="pin-keypad">
        {keys.map((k, i) => (
          k === '' ? <div key={i} /> :
          k === '⌫' ? (
            <button key={i} className="pin-key" onClick={handleBackspace} style={{ fontSize: 18 }}>⌫</button>
          ) : (
            <button key={i} className="pin-key" onClick={() => handleKey(k)}>{k}</button>
          )
        ))}
      </div>

      {onFullSignOut && (
        <button
          onClick={onFullSignOut}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: 'rgba(255,255,255,0.55)', fontSize: 13,
            textDecoration: 'underline', cursor: 'pointer'
          }}
        >
          Not {student.name}? Sign out
        </button>
      )}

      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Homework Underground</p>

      <style>{`
        @keyframes shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)}
          60%{transform:translateX(-8px)}
          80%{transform:translateX(4px)}
        }
        .shake { animation: shake 0.5s ease; }
      `}</style>
    </div>
  );
}
